/**
 * Equipment socket provider (E2.2.S2) — where an equipped item's base mesh
 * attaches on the LOCAL player's avatar.
 *
 * PROVISIONAL, capsule-era: these transforms are placed against the current
 * placeholder avatar (the `THREE.Group` Game.ts builds — a 0.4 m capsule body
 * `body.position.y = 0.85` spanning y≈0..1.7, +Z forward per its facing nose
 * cone). They are NOT bone sockets. When the real CHR-026 rig lands (owner-paced
 * Track C), every socket is:
 *
 *   re-seated on CHR-026 bone sockets at E2.4; v1 renders ALWAYS-HELD/WORN.
 *
 * i.e. mainhand/offhand items are shown held (never stowed/sheathed — no stance
 * signal exists client-side; `AnimState = idle|move|dodge` carries no stow), and
 * worn armour is a rigid capsule-region overlay (never skinned) until the rig
 * deforms it. The socket *names* match the live `EQUIPMENT_SLOTS`
 * (`shared/src/content/types.ts`: mainhand · offhand · head · chest · hands ·
 * legs · feet · trinket) so the E2.4 re-seat is a value change, not an API
 * change. `trinket` has no visible worn mesh in v1, so it carries no socket
 * (resolving it yields no attachment — the graceful path).
 *
 * Node-safe by design (like loader.ts/lod.ts): this module imports no `three`
 * runtime. It applies a transform to a minimal structural `SocketTarget`, which
 * a real `THREE.Object3D` satisfies (`.position.set` / `.rotation.set` /
 * `.scale.setScalar`) and a test fake satisfies too — so the whole attach path
 * is exercisable under `node --test` (AD-120), no GPU/DOM.
 */

/** A provisional local-space transform (avatar-group frame: +Z forward, +Y up). */
export interface SocketTransform {
  /** Local position [x, y, z] in metres, relative to the avatar group origin. */
  readonly position: readonly [number, number, number];
  /** Local Euler rotation [x, y, z] in radians (XYZ order — the three.js default). */
  readonly rotation: readonly [number, number, number];
  /** Uniform scale (1 = author scale; bases are modelled 1 unit = 1 m, EQP §6). */
  readonly scale: number;
}

/**
 * The structural shape the socket provider writes onto — the transform slice of
 * a `THREE.Object3D`. Kept minimal so this module (and its tests) stay free of
 * any `three` import; a real Object3D and a test fake both satisfy it.
 */
export interface SocketTarget {
  position: { set(x: number, y: number, z: number): unknown };
  rotation: { set(x: number, y: number, z: number): unknown };
  scale: { setScalar(s: number): unknown };
}

// Held-prop pitch: a gentle up-and-forward carry so a weapon/offhand reads as
// held-at-the-ready off the hand rather than lying flat along the aim axis
// (bases are authored +Z-forward at the grip, EQP-002 §4.2). PROVISIONAL — the
// real per-weapon grip pose is an AP5 concern re-seated onto the bone socket.
const HELD_PITCH = -Math.PI / 4;

/**
 * The mainhand (right-hand grip) held-prop socket — the grip a 1H weapon and a
 * gather tool share (EQP §4.1 / EQP-025 §4). Exported as a definite
 * `SocketTransform` so a caller can use it as a non-optional default: indexed
 * access on the `EQUIPMENT_SOCKETS` record is `SocketTransform | undefined` under
 * `noUncheckedIndexedAccess`, but this named grip always exists. Re-seated on the
 * CHR-026 bone socket at E2.4 along with the rest of the map.
 */
export const MAINHAND_SOCKET: SocketTransform = {
  position: [-0.45, 0.95, 0.12],
  rotation: [HELD_PITCH, 0, 0],
  scale: 1,
};

/**
 * The provisional capsule-era socket map, keyed by `EQUIPMENT_SLOTS` name.
 *
 *   re-seated on CHR-026 bone sockets at E2.4; v1 renders ALWAYS-HELD/WORN.
 *
 * Positions are eyeballed onto the placeholder capsule (radius 0.4 m, body
 * centre y≈0.85, total height ≈1.7 m): hands at ≈0.95 m to either side; worn
 * regions centred on head/torso/forearms/thighs/feet. Chirality (mainhand =
 * the avatar's right, offhand = left) and every offset are provisional.
 */
export const EQUIPMENT_SOCKETS: Readonly<Record<string, SocketTransform>> = {
  // Held props — one per hand, opposite sides, gentle up-forward carry.
  mainhand: MAINHAND_SOCKET,
  offhand: { position: [0.45, 0.95, 0.12], rotation: [HELD_PITCH, 0, 0], scale: 1 },
  // Worn armour — rigid capsule-region overlays (skinned deformation is E2.4).
  head: { position: [0, 1.55, 0], rotation: [0, 0, 0], scale: 1 },
  chest: { position: [0, 1.05, 0.02], rotation: [0, 0, 0], scale: 1 },
  hands: { position: [0, 0.95, 0.05], rotation: [0, 0, 0], scale: 1 },
  legs: { position: [0, 0.55, 0], rotation: [0, 0, 0], scale: 1 },
  feet: { position: [0, 0.12, 0.05], rotation: [0, 0, 0], scale: 1 },
  // `trinket` intentionally absent — no visible worn mesh in v1.
};

/**
 * The provisional socket transform for an equipment slot, or `undefined` when
 * the slot has no visible socket (`trinket`, an unknown/empty slot) — the caller
 * then attaches nothing (graceful, per the E2.2 fallback contract).
 */
export function socketForSlot(slot: string | null | undefined): SocketTransform | undefined {
  if (slot == null) return undefined;
  return EQUIPMENT_SOCKETS[slot];
}

/** Write a socket transform onto a target object (in place). */
export function applySocket(target: SocketTarget, socket: SocketTransform): void {
  target.position.set(socket.position[0], socket.position[1], socket.position[2]);
  target.rotation.set(socket.rotation[0], socket.rotation[1], socket.rotation[2]);
  target.scale.setScalar(socket.scale);
}
